import { type CSSProperties } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Cross2Icon } from '@radix-ui/react-icons';
import { useTranslation } from 'react-i18next';
import { Button, Image, Text } from '@/shared/ui';

interface SortableImageItemProps {
  id: string;
  src: string;
  alt: string;
  onRemove: () => void;
  isFirst?: boolean;
}

/**
 * Draggable gallery thumbnail with a remove control and an optional
 * cover badge on the first image. Wraps the dnd-kit sortable behaviour.
 */
export function SortableImageItem({ id, src, alt, onRemove, isFirst }: SortableImageItemProps) {
  const { t } = useTranslation('features/properties');
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    outline: isDragging ? '2px solid var(--seed-base)' : 'none',
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners} className="relative rounded-(--radius-md) overflow-hidden aspect-square cursor-grab">
      <Image src={src} alt={alt} className="w-full h-full object-cover" />
      <Button
        variant="none"
        disableSize
        type="button"
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        className="absolute top-1 right-1 w-5 h-5 rounded-full bg-[var(--overlay-scrim)] flex items-center justify-center"
      >
        <Cross2Icon className="w-3 h-3 text-[var(--text-inverse)]" />
      </Button>
      {isFirst && (
        <Text
          component="span"
          className="absolute bottom-1 left-1 text-(--text-micro) font-bold px-(--space-1-5) py-(--space-0-5) rounded-(--radius-sm) bg-[var(--seed-base)] text-[var(--text-inverse)] uppercase tracking-wide"
        >
          {t('form.fieldLabels.cover')}
        </Text>
      )}
    </div>
  );
}
